/** What each stage is and who enters it, wave by wave (docs/spec/02 §4, §7; 03 §1). */

import type { ChallengeFigure } from './paths';
import { challengePath, entryPath } from './paths';
import type { Difficulty } from './tuning';
import { CHALLENGE, ENTRY, NOVA } from './tuning';
import type { EnemyKind, Mode, Spawn, StageKind } from './types';

export function stageKind(mode: Mode, stage: number): StageKind {
  if (mode === 'supernova' && stage % NOVA.empressEvery === 0) return 'empress';
  return stage % 4 === 3 ? 'challenge' : 'formation';
}

type Slot = readonly [row: number, col: number];
type Member = readonly [kind: EnemyKind, row: number, col: number];

const moths = (slots: readonly Slot[]): Member[] => slots.map(([r, c]) => ['moth', r, c] as const);
const stingers = (slots: readonly Slot[]): Member[] => slots.map(([r, c]) => ['stinger', r, c] as const);

/** Wave 2 alternates Monarch and Moth down one stream. */
const WAVE_2: readonly Member[] = [
  ['monarch', 0, 3], ['moth', 1, 3], ['monarch', 0, 4], ['moth', 2, 3],
  ['monarch', 0, 5], ['moth', 1, 6], ['monarch', 0, 6], ['moth', 2, 6],
];

interface Stream {
  readonly from: 'top' | 'side';
  readonly mirror: boolean;
  readonly members: readonly Member[];
  /** A lone stream may fly two files wide on even stages; the opening pair never does. */
  readonly lone: boolean;
  readonly extraKind: EnemyKind | null;
}

const WAVES: readonly (readonly Stream[])[] = [
  [
    { from: 'top', mirror: true, members: moths([[1, 4], [1, 5], [2, 4], [2, 5]]), lone: false, extraKind: null },
    { from: 'top', mirror: false, members: stingers([[3, 4], [3, 5], [4, 4], [4, 5]]), lone: false, extraKind: null },
  ],
  [{ from: 'side', mirror: false, members: WAVE_2, lone: true, extraKind: null }],
  [{
    from: 'side', mirror: true, lone: true, extraKind: 'moth',
    members: moths([[1, 1], [1, 2], [1, 7], [1, 8], [2, 1], [2, 2], [2, 7], [2, 8]]),
  }],
  [{
    from: 'top', mirror: false, lone: true, extraKind: 'stinger',
    members: stingers([[3, 2], [3, 3], [3, 6], [3, 7], [4, 2], [4, 3], [4, 6], [4, 7]]),
  }],
  [{
    from: 'top', mirror: true, lone: true, extraKind: 'stinger',
    members: stingers([[3, 0], [3, 1], [3, 8], [3, 9], [4, 0], [4, 1], [4, 8], [4, 9]]),
  }],
];

/** Ticks between enemies of a stream flying at `speed`. */
function gapTicks(speed: number): number {
  return Math.max(1, Math.round(ENTRY.spacing / speed));
}

/** The five entry waves of a formation stage. */
export function formationWaves(stage: number, d: Difficulty): Spawn[][] {
  const gap = gapTicks(d.entrySpeed);
  const twoFiles = stage % 2 === 0;
  return WAVES.map((streams) => {
    const wave: Spawn[] = [];
    for (const st of streams) {
      const path = entryPath(st.from, st.mirror);
      const files = twoFiles && st.lone;
      const place = (i: number): { at: number; side: number } =>
        files ? { at: Math.floor(i / 2) * gap, side: (i % 2 === 0 ? -1 : 1) * ENTRY.fileOffset } : { at: i * gap, side: 0 };
      st.members.forEach(([kind, row, col], i) => {
        wave.push({ ...place(i), kind, variant: 0, path, slotted: true, row, col, after: 'home' });
      });
      if (st.extraKind !== null) {
        for (let k = 0; k < d.extras; k++) {
          const at = place(st.members.length + k);
          wave.push({ ...at, kind: st.extraKind, variant: 0, path, slotted: false, row: 0, col: 0, after: 'attack' });
        }
      }
    }
    return wave.sort((a, b) => a.at - b.at);
  });
}

type Sides = 'both' | 'left' | 'right';

/** Three programmes of five figures; challenging stages take them in turn. */
const PROGRAMMES: readonly (readonly (readonly [ChallengeFigure, Sides])[])[] = [
  [['loop', 'both'], ['sweep', 'left'], ['sweep', 'right'], ['wave', 'both'], ['eight', 'both']],
  [['wave', 'both'], ['loop', 'left'], ['loop', 'right'], ['eight', 'both'], ['sweep', 'both']],
  [['eight', 'both'], ['wave', 'left'], ['wave', 'right'], ['sweep', 'both'], ['loop', 'both']],
];

/** Who flies the `index`-th challenging stage: Stingers, Moths, then the three morphs, and round again. */
export function challengeSkin(index: number): { kind: EnemyKind; variant: number } {
  const i = (((index - 1) % 5) + 5) % 5;
  if (i === 0) return { kind: 'stinger', variant: 0 };
  if (i === 1) return { kind: 'moth', variant: 0 };
  return { kind: 'morph', variant: i - 2 };
}

/** How fast the `index`-th challenging stage flies. */
export function challengeSpeed(index: number): number {
  return Math.min(4, CHALLENGE.speed + 0.1 * (index - 1));
}

/** The five waves of the `index`-th challenging stage (1 or more). */
export function challengeWaves(index: number): Spawn[][] {
  const programme = PROGRAMMES[(index - 1) % PROGRAMMES.length] ?? [];
  const skin = challengeSkin(index);
  const gap = gapTicks(challengeSpeed(index));
  return programme.map(([figure, sides]) => {
    const wave: Spawn[] = [];
    const stream = (mirror: boolean, count: number, delay: number): void => {
      const path = challengePath(figure, mirror);
      for (let i = 0; i < count; i++) {
        wave.push({ at: delay + i * gap, ...skin, path, side: 0, slotted: false, row: 0, col: 0, after: 'leave' });
      }
    };
    if (sides === 'both') {
      stream(false, 4, 0);
      // Half a beat behind, so the two streams interleave where their paths share a line.
      stream(true, 4, Math.floor(gap / 2));
    } else stream(sides === 'right', 8, 0);
    return wave.sort((a, b) => a.at - b.at);
  });
}
